import { createSlice } from "@reduxjs/toolkit";
import {
  getSpeakerType,
  cleanPosition,
} from "../DAIEditor/components/EditSpeaker/helpers";

const parseSpeakerName = (speakerName) => {
  if (!speakerName) {
    return { firstName: "", lastName: "", position: "", company: "", type: "" };
  }

  const positionMatch = speakerName.match(/\[([^\]]+)\]/);
  const positionRaw = positionMatch ? positionMatch[1].trim() : "";

  const companyMatch = speakerName.match(/\(([^)]+)\)/);
  const company = companyMatch ? companyMatch[1].trim() : "";

  let cleanName = speakerName
    .replace(/\[([^\]]+)\]/g, "")
    .replace(/\(([^)]+)\)/g, "")
    .trim();

  const nameParts = cleanName.split(/\s+/).filter((part) => part.length > 0);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";
  const inferredType = getSpeakerType(speakerName);

  return {
    firstName,
    lastName,
    position: cleanPosition(positionRaw),
    company,
    type: inferredType || "",
  };
};

const initialState = {
  list: [],
  typesByTag: {},
};

const buildTypesByTag = (list) => {
  const map = {};
  (list || []).forEach((s) => {
    if (s && s.tag) {
      map[s.tag] = s.type || "";
    }
  });
  return map;
};

const speakerSlice = createSlice({
  name: "speaker",
  initialState,
  reducers: {
    reset: () => initialState,

    initializeFromProcessedSpeakers: (state, action) => {
      const incoming = Array.isArray(action.payload?.speakers)
        ? action.payload.speakers
        : [];

      const detailed = incoming.map((s, index) => {
        const parsed = parseSpeakerName(s?.name || "");
        return {
          id: index + 1,
          tag: s?.tag || "",
          name: s?.name || "",
          speaker: s?.name || `Speaker ${index + 1}`,
          type: parsed.type || "",
          firstName: parsed.firstName || "",
          lastName: parsed.lastName || "",
          company: parsed.company || "",
          position: parsed.position || "",
          expanded: false,
        };
      });

      state.list = detailed;
      state.typesByTag = buildTypesByTag(detailed);
    },

    replaceAllRawSpeakers: (state, action) => {
      const next = Array.isArray(action.payload) ? action.payload : [];
      const normalized = next.map((s, idx) => ({
        id: s.id ?? idx + 1,
        tag: s.tag ?? "",
        name: s.name ?? s.speaker ?? "",
        speaker: s.speaker ?? s.name ?? "",
        type: s.type ?? "",
        firstName: s.firstName ?? "",
        lastName: s.lastName ?? "",
        company: s.company ?? "",
        position: s.position ?? "",
        expanded: !!s.expanded,
        validation: s.validation,
      }));
      state.list = normalized;
      state.typesByTag = buildTypesByTag(normalized);
    },

    applySavedFormattedSpeakers: (state, action) => {
      const formatted = Array.isArray(action.payload) ? action.payload : [];
      const updated = state.list.map((s, i) => {
        const f = formatted[i];
        if (f) {
          return {
            ...s,
            tag: s.tag,
            name: f.name ?? s.name,
            speaker: f.name ?? s.speaker,
          };
        }
        return s;
      });
      state.list = updated;
      state.typesByTag = buildTypesByTag(updated);
    },
  },
});

export const {
  reset,
  initializeFromProcessedSpeakers,
  replaceAllRawSpeakers,
  applySavedFormattedSpeakers,
} = speakerSlice.actions;

export const selectSpeakersRaw = (state) => state.speaker.list;
export const selectSpeakerTypesByTag = (state) => state.speaker.typesByTag;

export default speakerSlice.reducer;
